import React, { useEffect, useMemo, useState, useRef } from 'react';
import { HouseLine, CaretDown, MagnifyingGlass, Plus, X, Check } from '@phosphor-icons/react';
import { cn } from '../../../lib/utils';
interface PropertyAnchorRowProps {
  properties: any[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onAddProperty?: () => void;
}
/**
 * Searchable dropdown that anchors the marketplace search to ONE of the
 * landlord's properties. No "All properties" option — default state is
 * simply unselected. Footer always shows "+ Adaugă proprietate nouă"
 * so the user can route to the PropertyEditor without leaving the flow.
 */
export function PropertyAnchorRow({
  properties,
  selectedId,
  onSelect,
  onAddProperty
}: PropertyAnchorRowProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Auto-focus the search input on open
  useEffect(() => {
    if (isOpen) {
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    } else {
      setSearch('');
    }
  }, [isOpen]);
  const getLabel = (p: any) => p?.title || p?.listingTitle || p?.name || 'Proprietate fără nume';
  const getPrice = (p: any) => p?.priceEur ? `${p.priceEur} €` : p?.price ? `${p.price} RON` : null;
  const getLocation = (p: any) => [p?.neighborhood, p?.city].filter(Boolean).join(', ');
  const selectedProperty = useMemo(() => properties.find(p => p.id === selectedId) || null, [properties, selectedId]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return properties;
    return properties.filter(p => {
      const haystack = [getLabel(p), getLocation(p), getPrice(p)].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [properties, search]);
  const handleClearSelection = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(null);
  };
  const handleSelect = (id: string) => {
    onSelect(id);
    setIsOpen(false);
  };
  const handleAdd = () => {
    setIsOpen(false);
    onAddProperty?.();
  };
  const hasNoProperties = !properties || properties.length === 0;
  return <div className="relative w-full max-w-md" ref={ref} data-id="element-2428">
      <button type="button" onClick={() => setIsOpen(v => !v)} className={cn('w-full h-11 px-4 rounded-xl border text-sm flex items-center gap-3 transition-colors cursor-pointer', selectedProperty ? 'bg-dark-2 border-lime-1 text-lime-1' : 'bg-dark-3 border-dark-4 text-light-2 hover:border-light-4')} data-id="element-2429">
        <HouseLine size={18} weight={selectedProperty ? 'fill' : 'regular'} className="shrink-0" data-id="element-2430" />
        {selectedProperty ? <span className="flex-1 truncate text-left" data-id="element-2431">
            <span className="font-medium" data-id="element-2432">{getLabel(selectedProperty)}</span>
            {getPrice(selectedProperty) && <span className="text-light-3 font-normal ml-2" data-id="element-2433">
                · {getPrice(selectedProperty)}
              </span>}
          </span> : <span className="flex-1 truncate text-left text-light-3" data-id="element-2434">
            Selectează proprietatea pentru care cauți chiriaș
          </span>}

        {selectedProperty ? <button type="button" onClick={handleClearSelection} aria-label="Elimină proprietatea" className="shrink-0 p-1 -m-1 text-lime-1 hover:text-light-1 transition-colors cursor-pointer" data-id="element-2435">
            <X size={14} weight="bold" data-id="element-2436" />
          </button> : <CaretDown size={14} className={cn('shrink-0 transition-transform', isOpen && 'rotate-180')} data-id="element-2437" />}
      </button>

      {isOpen && <div className="absolute top-full left-0 mt-2 w-[min(420px,100%)] bg-dark-2 border border-white/15 rounded-xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 flex flex-col max-h-[420px]" data-id="element-2438">
          {/* Search input — hidden when no properties exist */}
          {!hasNoProperties && <div className="p-3 border-b border-dark-4 shrink-0" data-id="element-2439">
              <div className="relative" data-id="element-2440">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-light-4" data-id="element-2441" />
                <input ref={inputRef} type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Caută o proprietate..." className="w-full h-9 bg-dark-3 border border-dark-4 rounded-lg pl-9 pr-3 text-sm text-light-1 placeholder:text-light-4 focus:outline-none focus:border-lime-1 focus:ring-1 focus:ring-lime-1" data-id="element-2442" />
              </div>
            </div>}

          {/* List */}
          <div className="flex-1 overflow-y-auto" data-id="element-2443">
            {hasNoProperties ? <div className="px-4 py-8 text-center" data-id="element-2444">
                <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-dark-3 flex items-center justify-center" data-id="element-2445">
                  <HouseLine size={22} weight="duotone" className="text-light-3" data-id="element-2446" />
                </div>
                <p className="text-sm text-light-2 mb-1 font-medium" data-id="element-2447">
                  Nu ai proprietăți încă
                </p>
                <p className="text-xs text-light-4 leading-relaxed" data-id="element-2448">
                  Adaugă o proprietate ca să cauți chiriași potriviți pentru ea.
                </p>
              </div> : filtered.length === 0 ? <div className="px-4 py-8 text-center" data-id="element-2449">
                <p className="text-sm text-light-3" data-id="element-2450">
                  Nicio proprietate găsită pentru „{search}".
                </p>
              </div> : <ul className="py-1" data-id="element-2451">
                {filtered.map(p => {
            const isSel = selectedId === p.id;
            const label = getLabel(p);
            const price = getPrice(p);
            const location = getLocation(p);
            return <li key={p.id} data-id="element-2452">
                      <button type="button" onClick={() => handleSelect(p.id)} className={cn('w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors cursor-pointer', isSel ? 'bg-dark-3' : 'hover:bg-dark-3')} data-id="element-2453">
                        <div className={cn('w-9 h-9 rounded-lg flex items-center justify-center shrink-0', isSel ? 'bg-lime-1/15 text-lime-1' : 'bg-dark-3 text-light-3')} data-id="element-2454">
                          <HouseLine size={18} weight={isSel ? 'fill' : 'regular'} data-id="element-2455" />
                        </div>
                        <div className="min-w-0 flex-1" data-id="element-2456">
                          <div className={cn('text-sm font-medium truncate', isSel ? 'text-lime-1' : 'text-light-1')} data-id="element-2457">
                            {label}
                          </div>
                          <div className="text-xs text-light-3 truncate mt-0.5" data-id="element-2458">
                            {[price, location].filter(Boolean).join(' · ') || 'Fără detalii'}
                          </div>
                        </div>
                        {isSel && <Check size={16} weight="bold" className="text-lime-1 shrink-0" data-id="element-2459" />}
                      </button>
                    </li>;
          })}
              </ul>}
          </div>

          {/* Sticky footer — always shows the add CTA */}
          {onAddProperty && <div className="border-t border-white/10 shrink-0 bg-dark-2" data-id="element-2460">
              <button type="button" onClick={handleAdd} className="w-full flex items-center gap-2 px-4 py-3 text-sm font-medium text-lime-1 hover:bg-dark-3 transition-colors cursor-pointer" data-id="element-2461">
                <Plus size={16} weight="bold" data-id="element-2462" />
                Adaugă proprietate nouă
              </button>
            </div>}
        </div>}
    </div>;
}